/**
 * Collections transport: the dunning ladder, the dunning sweep, and customer
 * statement runs.
 *
 * Three contract facts the screen is built on:
 *
 *   • The ladder is RECONCILED, not patched. A level the caller drops is
 *     deactivated rather than deleted, because a dunning event's level must
 *     stay explicable years after the ladder changed.
 *   • The dunning sweep is structurally idempotent: `(invoice, level)` is
 *     unique, so a rerun on the same day inserts nothing and therefore sends
 *     nothing. There is no "already sent" error to report — there is simply
 *     nothing to report.
 *   • A statement run is queued, not rendered inline. The 202 carries a run row
 *     whose counts fill in as the job works, which is why the screen polls it
 *     rather than claiming the statements are out.
 */
import type { components } from "@erp/api-client";

import { apiFetch } from "@/lib/api/http";

type Schemas = components["schemas"];

export type DunningLevel = Schemas["DunningLevel"];
export type DunningLevelSet = Schemas["DunningLevelSet"];
export type DunningRunAccepted = Schemas["DunningRunAccepted"];
export type CustomerStatementRun = Schemas["CustomerStatementRun"];

/** The templates the notification lane has ratified. A level naming anything
 *  else configures a message nothing will render. */
export const DUNNING_TEMPLATES = ["dunning_level_1", "dunning_level_2", "dunning_level_3"] as const;

export function listDunningLevels(signal?: AbortSignal): Promise<DunningLevelSet> {
  return apiFetch<DunningLevelSet>("/dunning-levels", { signal });
}

/** Reconciles the ladder to exactly this set; anything absent is deactivated. */
export function replaceDunningLevels(items: readonly DunningLevel[]): Promise<DunningLevelSet> {
  return apiFetch<DunningLevelSet>("/dunning-levels", {
    method: "PUT",
    body: { items },
  });
}

export function runDunning(
  body: { as_of?: string },
  idempotencyKey: string,
): Promise<DunningRunAccepted> {
  return apiFetch<DunningRunAccepted>("/dunning-runs", { method: "POST", body, idempotencyKey });
}

export function runCustomerStatements(
  body: { currency: string; as_of?: string; branch_id?: string | null },
  idempotencyKey: string,
): Promise<CustomerStatementRun> {
  return apiFetch<CustomerStatementRun>("/customer-statements/runs", {
    method: "POST",
    body,
    idempotencyKey,
  });
}

export function getCustomerStatementRun(
  runId: string,
  signal?: AbortSignal,
): Promise<CustomerStatementRun> {
  return apiFetch<CustomerStatementRun>(`/customer-statements/runs/${encodeURIComponent(runId)}`, {
    signal,
  });
}

/** A ladder only escalates if each rung waits longer than the one before it.
 *  The server does not reject a ladder that does not, so the screen says so. */
export function ladderEscalates(items: readonly { min_days_overdue: number }[]): boolean {
  for (let index = 1; index < items.length; index += 1) {
    const previous = items[index - 1];
    const current = items[index];
    if (previous === undefined || current === undefined) continue;
    if (current.min_days_overdue <= previous.min_days_overdue) return false;
  }
  return true;
}
